import { inject } from '@angular/core';
import { CanActivateFn, Router, type UrlTree } from '@angular/router';
import { map } from 'rxjs/operators';
import { SUPER_ADMIN_PERMISSION_SLUGS as P } from '../constants/super-admin-permission-slugs';
import { PermissionConfigService } from '../services/permission-config.service';
import { PermissionService } from '../services/permission.service';

/**
 * Candidate landing pages in priority order. Used when a denied route's own
 * fallback is itself gated — without this, gating `/dashboard` would bounce a
 * user straight back to the page they were just refused.
 */
const LANDING_ROUTES: ReadonlyArray<{ path: string; permissions: string[] }> = [
  { path: '/dashboard', permissions: [P.DASHBOARD_VIEW] },
  { path: '/manage-agent', permissions: [P.AGENT_LIST, P.AGENT_ARCHIVED_LIST] },
  { path: '/manage-client', permissions: [P.TENANT_LIST] },
  { path: '/manage-admin-users', permissions: [P.USER_LIST] },
  { path: '/audit-trail', permissions: [P.AUDIT_TRAIL_LIST, P.AUDIT_TRAIL_VIEW] },
  { path: '/reports', permissions: [P.REPORT_LIST, P.REPORT_VIEW] },
];

/** Ungated route, so it always terminates the search. */
const LAST_RESORT_ROUTE = '/user-account-settings';

function resolveFallback(
  permissionService: PermissionService,
  requested: string,
): string {
  const known = LANDING_ROUTES.find((r) => r.path === requested);
  // An unlisted fallback is a sibling page of the route that was denied
  // (e.g. `/manage-agent` for an agent detail view) — honour it as-is.
  if (!known) {
    return requested;
  }
  if (permissionService.hasAnyPermission(known.permissions)) {
    return requested;
  }
  const reachable = LANDING_ROUTES.find((r) =>
    permissionService.hasAnyPermission(r.permissions),
  );
  return reachable?.path ?? LAST_RESORT_ROUTE;
}

/**
 * Route `data`:
 * - `permissions`: string | string[] — slug(s) required to open the route
 * - `requireAll` (optional): if true, user must have every slug; otherwise any one is enough
 * - `fallbackRoute` (optional): redirect when denied (default `/dashboard`, downgraded
 *   to the first reachable landing page when the user cannot open it either)
 */
export const permissionGuard: CanActivateFn = (route): ReturnType<CanActivateFn> => {
  const permissionService = inject(PermissionService);
  const permissionConfig = inject(PermissionConfigService);
  const router = inject(Router);

  const required = route.data['permissions'] as string | string[] | undefined;
  const requireAll = Boolean(route.data['requireAll']);
  const fallback = (route.data['fallbackRoute'] as string) || '/dashboard';
  if (!required) {
    return true;
  }

  const cfg = permissionConfig.getConfig();
  if (!cfg.enablePermissionChecks || cfg.bypassAllPermissions) {
    return true;
  }

  const list = Array.isArray(required) ? required : [required];

  return permissionService.ensureLoaded().pipe(
    map((): boolean | UrlTree => {
      const ok = requireAll
        ? permissionService.hasAllPermissions(list)
        : permissionService.hasAnyPermission(list);
      return ok
        ? true
        : router.createUrlTree([resolveFallback(permissionService, fallback)]);
    }),
  );
};
